/**
 * Normalize chat API / session payload into layered answer presentation fields.
 * Summary is shown by default; details stay collapsed unless explicitly expanded.
 */
export function buildAssistantAnswerPresentation(payload = {}) {
  const summaryRaw =
    typeof payload.answer_summary === 'string' ? payload.answer_summary.trim() : '';
  const hasLayered = Boolean(summaryRaw);
  // When layered summary is present, only use explicit answer_details.
  // Never fall back to message/content — that reintroduces a second narrative.
  let detailsRaw = '';
  if (typeof payload.answer_details === 'string') {
    detailsRaw = payload.answer_details;
  } else if (!hasLayered) {
    detailsRaw = payload.message || payload.content || '';
  }
  const detailsTrimmed = String(detailsRaw || '').trim();
  return {
    text: hasLayered ? summaryRaw : String(payload.message || payload.content || ''),
    answerSummary: hasLayered ? summaryRaw : null,
    answerDetails: hasLayered ? detailsTrimmed : null,
    // Collapsed by default whenever layered presentation is active.
    detailsCollapsed: hasLayered ? payload.details_collapsed !== false : null,
    defaultActiveKey: hasLayered && payload.details_collapsed === false
      ? ['api-flow-details']
      : [],
  };
}

/**
 * Build clipboard text for an assistant message.
 * Includes collapsed details when layered presentation is present.
 * Avoid re-labeling when details already start with "API Flow:".
 */
export function buildAssistantCopyText(msg = {}) {
  const summary = String(msg.answerSummary || msg.text || '').trim();
  const details = String(msg.answerDetails || '').trim();
  if (!details) {
    return summary;
  }
  if (!summary) {
    return details;
  }
  // Avoid duplicating when summary and details are identical.
  if (summary === details) {
    return summary;
  }
  // Avoid inserting the Summary paragraph again if details already contain it.
  if (details.startsWith(summary)) {
    return details;
  }
  const normalizedSummary = summary.replace(/\s+/g, ' ').trim().toLowerCase();
  const firstPara = details.split(/\n\n/)[0].replace(/\s+/g, ' ').trim().toLowerCase();
  if (normalizedSummary && firstPara === normalizedSummary) {
    return details;
  }
  // Details already expose the API Flow section — do not wrap with a second heading.
  if (/^API Flow:/i.test(details)) {
    return `${summary}\n\n${details}`;
  }
  return `${summary}\n\nAPI Flow Details\n\n${details}`;
}
